import { Link, useLocation } from "react-router-dom";
import { ExternalLink, Ruler } from "lucide-react";
import { useState } from "react";
import SizeGuideModal from "@/components/SizeGuideModal";

// Custom social icons with brand-flavored glow
function IGIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <rect x="3" y="3" width="18" height="18" rx="5" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="1.8" />
      <circle cx="17.5" cy="6.5" r="1.2" fill="currentColor" />
    </svg>
  );
}

function TikTokIcon({ size = 22 }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M19.6 6.8a5.6 5.6 0 0 1-3.4-1.2 5.6 5.6 0 0 1-2.1-3.6h-3v13.4a3 3 0 1 1-3-3c.3 0 .6 0 .9.1V9.2a6.4 6.4 0 1 0 5.4 6.3V9.3a8.6 8.6 0 0 0 5.2 1.7V7.9c0-.4 0-.7 0-1.1Z" />
    </svg>
  );
}

function SocialPill({ href, label, Icon, accent }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className={`group inline-flex items-center gap-2.5 px-4 py-2.5 border transition-all duration-200 ${accent}`}
      data-testid={`social-${label.toLowerCase()}`}
    >
      <span className="transition-transform duration-200 group-hover:scale-110">
        <Icon size={20} />
      </span>
      <span className="text-sm uppercase tracking-widest">{label}</span>
    </a>
  );
}

export default function Footer() {
  const location = useLocation();
  const [sizeGuideOpen, setSizeGuideOpen] = useState(false);
  if (location.pathname.startsWith("/admin")) return null;

  return (
    <footer className="border-t border-white/5 mt-32 pt-16 pb-10 px-5 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-3 gap-12">
          <div>
            <h3 className="font-display text-2xl mb-3 text-magenta">FLUO Neuchâtel</h3>
            <p className="text-sm text-white/60 leading-relaxed">
              Le festival urbain au bord du lac de Neuchâtel.
              <br />
              Édition 2027.
            </p>
          </div>

          <div>
            <h4 className="uppercase text-xs tracking-widest text-white/40 mb-4">
              Liens
            </h4>
            <ul className="space-y-2 text-sm">
              <li>
                <a
                  href="https://www.fluoneuchatel.com"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-magenta inline-flex items-center gap-1"
                >
                  Site officiel <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <a
                  href="https://www.fluoneuchatel.com/billetterie"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-magenta inline-flex items-center gap-1"
                >
                  Billetterie <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <a
                  href="https://www.fluoneuchatel.com/line-up"
                  target="_blank"
                  rel="noreferrer"
                  className="hover:text-magenta inline-flex items-center gap-1"
                >
                  Line-up <ExternalLink size={12} />
                </a>
              </li>
              <li>
                <button
                  type="button"
                  onClick={() => setSizeGuideOpen(true)}
                  className="hover:text-magenta inline-flex items-center gap-1 cursor-pointer"
                  data-testid="footer-size-guide"
                >
                  <Ruler size={12} /> Guide des tailles
                </button>
              </li>
              <li>
                <Link to="/admin/login" className="text-white/30 hover:text-white/60">
                  Espace admin
                </Link>
              </li>
            </ul>
          </div>

          <div>
            <h4 className="uppercase text-xs tracking-widest text-white/40 mb-4">
              Suivez-nous
            </h4>
            <div className="flex flex-wrap gap-2.5">
              <SocialPill
                href="https://www.instagram.com/fluoneuchatel/"
                label="Instagram"
                Icon={IGIcon}
                accent="border-white/30 text-white hover:bg-white hover:text-black hover:border-white"
              />
              <SocialPill
                href="https://www.tiktok.com/@fluoneuchatel"
                label="TikTok"
                Icon={TikTokIcon}
                accent="border-white/30 text-white hover:bg-white hover:text-black hover:border-white"
              />
            </div>
            <p className="text-xs text-white/40 mt-4">@fluoneuchatel</p>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-white/5 flex flex-col md:flex-row justify-between items-center gap-3 text-xs text-white/30">
          <span>Copyright © 2027 Terrible Style Production SÀRL. Tous droits réservés.</span>
          <span>Paiements sécurisés par Stripe · Livraison Suisse</span>
        </div>
      </div>
      <SizeGuideModal open={sizeGuideOpen} onClose={() => setSizeGuideOpen(false)} />
    </footer>
  );
}
